import React, { useEffect, useState } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2Icon } from 'lucide-react'

import { cn } from '@/shared/utils/helpers'

const spinnerVariants = cva('animate-spin text-primary', {
	variants: {
		size: {
			xs: 'size-3',
			sm: 'size-4',
			default: 'size-6',
			lg: 'size-8',
			xl: 'size-12',
		},
	},
	defaultVariants: {
		size: 'default',
	},
})

export interface SpinnerProps
	extends React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof spinnerVariants> {
	tip?: string
	spinning?: boolean
	delay?: number
}

const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
	(
		{ className, size, tip, spinning = true, delay = 0, children, ...props },
		ref
	) => {
		const [isDelayed, setIsDelayed] = useState(delay > 0)

		useEffect(() => {
			if (delay > 0 && spinning) {
				const timer = setTimeout(() => {
					setIsDelayed(false)
				}, delay)
				return () => clearTimeout(timer)
			}
			setIsDelayed(false)
		}, [delay, spinning])

		// If there are children, wrap them with spinner overlay
		if (children) {
			return (
				<div ref={ref} className="relative" {...props}>
					{children}
					{spinning && !isDelayed && (
						<div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
							<div className="flex flex-col items-center gap-2">
								<Loader2Icon
									className={cn(spinnerVariants({ size }), className)}
								/>
								{tip && <p className="text-sm text-muted-foreground">{tip}</p>}
							</div>
						</div>
					)}
				</div>
			)
		}

		// Standalone spinner
		if (!spinning || isDelayed) {
			return null
		}

		return (
			<div
				ref={ref}
				className={cn('flex flex-col items-center gap-2', className)}
				role="status"
				aria-live="polite"
				aria-busy={spinning}
				{...props}
			>
				<Loader2Icon className={spinnerVariants({ size })} />
				{tip && <p className="text-sm text-muted-foreground">{tip}</p>}
				<span className="sr-only">Loading...</span>
			</div>
		)
	}
)
Spinner.displayName = 'Spinner'

export { Spinner }
